import { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ALL_DAYS,
  DAY_NAMES,
  NEVER_CRON,
  ReportCronMode,
  buildWeeklyCron,
  describeReportCron,
  getNextRuns,
  parseReportCron,
} from "./reportCron";

type Props = {
  value?: string;
  onChange: (cronStatement: string) => void;
  error?: string;
};

const WEEKDAYS = ALL_DAYS.slice(0, 5);
const HOURS = Array.from({ length: 24 }, (_, i) => (i < 10 ? `0${i}` : `${i}`));
const MINUTES = ["00", "15", "30", "45"];

function formatRun(d: Date): string {
  return d.toLocaleString("nb-NO", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const ReportCronEditor: FC<Props> = ({ value, onChange, error }) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const initial = useMemo(() => (value ? parseReportCron(value) : { mode: "never" as const }), []);

  const [mode, setMode] = useState<ReportCronMode>(initial.mode);
  const [days, setDays] = useState<string[]>(initial.mode === "weekly" ? initial.days : ["MON"]);
  const [hour, setHour] = useState(initial.mode === "weekly" ? initial.hour : "08");
  const [minute, setMinute] = useState(initial.mode === "weekly" ? initial.minute : "00");
  const [advanced, setAdvanced] = useState(value ?? "");

  const cronStatement =
    mode === "never" ? NEVER_CRON : mode === "weekly" ? buildWeeklyCron(days, hour, minute) : advanced.trim();

  useEffect(() => {
    onChange(cronStatement);
  }, [cronStatement]);

  const changeMode = (next: ReportCronMode | null) => {
    if (!next || next === mode) return;
    if (next === "advanced") {
      setAdvanced(cronStatement);
    }
    if (next === "weekly" && mode === "advanced") {
      const parsed = parseReportCron(advanced);
      if (parsed.mode === "weekly") {
        setDays(parsed.days);
        setHour(parsed.hour);
        setMinute(parsed.minute);
      }
    }
    setMode(next);
  };

  const minuteOptions = MINUTES.includes(minute) ? MINUTES : [...MINUTES, minute].sort();
  const runs = getNextRuns(cronStatement);
  const advancedFitsWeekly = mode === "advanced" && parseReportCron(advanced).mode === "weekly";

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={{ xs: 1.5, sm: 2 }} flexWrap="wrap">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Rapport / test-tidspunkt</Typography>
            <Typography variant="body2" color="text.secondary">
              Når den fysiske testen kjøres. Norsk tid.
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            color="primary"
            size="small"
            value={mode}
            onChange={(_, v) => changeMode(v)}
            aria-label="Modus"
            sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiToggleButton-root": { flex: { xs: 1, sm: "none" }, height: { xs: 40, sm: "auto" } } }}
          >
            <ToggleButton value="never">Aldri</ToggleButton>
            <ToggleButton value="weekly">Ukentlig</ToggleButton>
            <ToggleButton value="advanced">Avansert</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {mode === "weekly" && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 3 }} flexWrap="wrap">
              <ToggleButtonGroup
                color="primary"
                value={days}
                onChange={(_, v: string[]) => setDays(ALL_DAYS.filter((d) => v.includes(d)))}
                aria-label="Dager"
                sx={{
                  display: { xs: "grid", sm: "flex" },
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: { xs: 0.5, sm: 0.75 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {ALL_DAYS.map((d) => (
                  <ToggleButton
                    key={d}
                    value={d}
                    aria-label={DAY_NAMES[d].full}
                    sx={{
                      height: { xs: 44, sm: 36 },
                      minWidth: { xs: 0, sm: 48 },
                      px: { xs: 0, sm: 1 },
                      borderRadius: { xs: "22px !important", sm: "18px !important" },
                      border: "1px solid !important",
                      borderColor: "divider",
                      textTransform: "none",
                    }}
                  >
                    {DAY_NAMES[d].short}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={{ xs: 1, sm: 3 }}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControl size="small" sx={{ minWidth: { xs: 76, sm: 84 } }}>
                    <InputLabel id="report-cron-hour">Time</InputLabel>
                    <Select labelId="report-cron-hour" label="Time" value={hour} onChange={(e) => setHour(e.target.value)}>
                      {HOURS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography color="text.secondary">:</Typography>
                  <FormControl size="small" sx={{ minWidth: { xs: 76, sm: 84 } }}>
                    <InputLabel id="report-cron-minute">Minutt</InputLabel>
                    <Select labelId="report-cron-minute" label="Minutt" value={minute} onChange={(e) => setMinute(e.target.value)}>
                      {minuteOptions.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems="flex-end">
                  <Button size="small" onClick={() => setDays([...WEEKDAYS])}>Hverdager</Button>
                  <Button size="small" onClick={() => setDays([...ALL_DAYS])}>Alle dager</Button>
                </Box>
              </Box>
            </Box>
            {!days.length && (
              <Typography variant="body2" color="error">Velg minst én dag.</Typography>
            )}
          </Box>
        )}

        {mode === "never" && (
          <Alert severity="warning">
            <strong>Fysisk test kjøres aldri for denne siten.</strong> Alarmovervåking, watchdog og integrasjoner
            kjører fortsatt som normalt. Bytt til Ukentlig for å planlegge tester igjen.
          </Alert>
        )}

        {mode === "advanced" && (
          <Box display="flex" flexDirection="column" gap={1}>
            <TextField
              label="Cron-uttrykk (Quartz)"
              type="text"
              fullWidth
              value={advanced}
              onChange={(e) => setAdvanced(e.target.value)}
              inputProps={{ spellCheck: false, style: { fontFamily: "monospace" } }}
              error={!!advanced.trim() && !runs.length && parseReportCron(advanced).mode !== "never"}
              helperText="Format: sek min time dag-i-mnd mnd ukedag [år]. Sekund og år ignoreres av scheduleren."
            />
            {advancedFitsWeekly && (
              <Box>
                <Button size="small" onClick={() => changeMode("weekly")}>Uttrykket passer i Ukentlig – bytt</Button>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{ borderTop: 1, borderColor: "divider", pt: 1.5, display: "flex", alignItems: "baseline", columnGap: 2, rowGap: 0.75, flexWrap: "wrap" }}
        >
          <Typography sx={{ fontWeight: 500 }}>{cronStatement ? describeReportCron(cronStatement) : "—"}</Typography>
          <Box
            component="code"
            sx={{ fontFamily: "monospace", fontSize: 12, bgcolor: "grey.100", px: 0.75, py: 0.25, borderRadius: 1 }}
          >
            {cronStatement || "—"}
          </Box>
          {!runs.length ? (
            <Typography variant="body2" color="text.secondary">Ingen planlagte kjøringer</Typography>
          ) : isMobile ? (
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" color="text.secondary">Neste kjøringer</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, typography: "body2", lineHeight: 1.6 }}>
                {runs.map((d) => <li key={d.getTime()}>{formatRun(d)}</li>)}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Neste: {runs.map(formatRun).join(" · ")}
            </Typography>
          )}
        </Box>

        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </Box>
    </Paper>
  );
};

export default ReportCronEditor;
